import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import PinGate, { clearAdminSession } from './PinGate.jsx'
import {
  PbccHeroHeader,
  PbccAdminSidebar,
  PbccSectionTitle,
  fourBallNavItems,
} from '../components/PbccLayout.jsx'
import { trialSignOut } from '../auth/TrialGate.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import { watchTournaments } from '../lib/data.js'
import { watchQualifyingEvents } from '../lib/qualifyingEvents.js'
import CLUB from '../config/club.js'
import { FOURBALL_ONLY, IS_GOLF_ELITE } from '../config/siteMode.js'

// Golf Elite: frosted-glass panel to replace the deep-green/cream PBCC boxes.
const GE_GLASS = { background: 'linear-gradient(135deg, rgba(255,255,255,0.84), rgba(234,243,255,0.74))', border: '1px solid rgba(255,255,255,0.85)', boxShadow: '0 18px 44px rgba(8,37,78,0.14)', backdropFilter: 'blur(14px) saturate(125%)', WebkitBackdropFilter: 'blur(14px) saturate(125%)' }

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
// Deeper forest green for the welcome header + weather bars (matches the app's dark shell).
const DEEP_GREEN = 'var(--ge-primary)'

// Pine Bluff, AR — fixed coords for the free, no-key Open-Meteo forecast.
const PBCC_LAT = 34.2284
const PBCC_LON = -92.0032

// Windy interactive radar embed — free, no API key. Radar is the default
// layer; the embed's built-in layer picker switches to Wind (matters for golf).
const WINDY_SRC = `https://embed.windy.com/embed2.html?lat=${PBCC_LAT}&lon=${PBCC_LON}`
  + `&detailLat=${PBCC_LAT}&detailLon=${PBCC_LON}&zoom=8&level=surface&overlay=radar&product=radar`
  + `&menu=&message=&marker=true&calendar=now&pressure=&type=map&location=coordinates`
  + `&detail=&metricWind=mph&metricTemp=%C2%B0F&radarRange=-1`

// Session key so the radar's open/closed choice persists while the tab is open.
const RADAR_KEY = 'pbcc.welcome.radarOpen'

// WMO weather-code → [emoji, label]. Covers the common daytime conditions.
const WMO = {
  0: ['☀️', 'Clear'], 1: ['🌤️', 'Mainly clear'], 2: ['⛅', 'Partly cloudy'], 3: ['☁️', 'Overcast'],
  45: ['🌫️', 'Fog'], 48: ['🌫️', 'Rime fog'],
  51: ['🌦️', 'Light drizzle'], 53: ['🌦️', 'Drizzle'], 55: ['🌦️', 'Heavy drizzle'],
  56: ['🌧️', 'Freezing drizzle'], 57: ['🌧️', 'Freezing drizzle'],
  61: ['🌧️', 'Light rain'], 63: ['🌧️', 'Rain'], 65: ['🌧️', 'Heavy rain'],
  66: ['🌧️', 'Freezing rain'], 67: ['🌧️', 'Freezing rain'],
  71: ['🌨️', 'Light snow'], 73: ['🌨️', 'Snow'], 75: ['🌨️', 'Heavy snow'], 77: ['🌨️', 'Snow grains'],
  80: ['🌦️', 'Showers'], 81: ['🌦️', 'Showers'], 82: ['⛈️', 'Violent showers'],
  85: ['🌨️', 'Snow showers'], 86: ['🌨️', 'Snow showers'],
  95: ['⛈️', 'Thunderstorm'], 96: ['⛈️', 'Thunderstorm w/ hail'], 99: ['⛈️', 'Thunderstorm w/ hail'],
}

const fmtTime = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function WelcomePage({ weatherOnly = false }) {
  const navigate = useNavigate()
  const [tournaments, setTournaments] = useState({})
  const [qualifyingEvents, setQualifyingEvents] = useState({})
  const [weather, setWeather] = useState(null)
  const [weatherFailed, setWeatherFailed] = useState(false)
  // Radar collapsed by default; remember the choice for the session. everOpened
  // defers mounting the (heavy) Windy iframe until first opened, then keeps it
  // mounted so re-opening is instant and the collapse animates smoothly.
  const [radarOpen, setRadarOpen] = useState(() => {
    try { return sessionStorage.getItem(RADAR_KEY) === '1' } catch { return false }
  })
  const [everOpened, setEverOpened] = useState(radarOpen)
  const toggleRadar = () => setRadarOpen((o) => {
    const next = !o
    if (next) setEverOpened(true)
    try { sessionStorage.setItem(RADAR_KEY, next ? '1' : '0') } catch { /* ignore */ }
    return next
  })

  useEffect(() => { const off = watchTournaments(setTournaments); return () => off() }, [])
  useEffect(() => { const off = watchQualifyingEvents(setQualifyingEvents); return () => off() }, [])
  useEffect(() => {
    // Open-Meteo: free, no API key required.
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${PBCC_LAT}&longitude=${PBCC_LON}`
      + `&current=temperature_2m,weather_code,apparent_temperature,relative_humidity_2m,wind_speed_10m,wind_direction_10m`
      + `&hourly=temperature_2m,precipitation_probability,weather_code,wind_speed_10m`
      + `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max,sunrise,sunset`
      + `&timezone=America/Chicago&temperature_unit=fahrenheit&wind_speed_unit=mph&forecast_days=10`
    fetch(url)
      .then((r) => r.json())
      .then((d) => setWeather(d))
      .catch(() => setWeatherFailed(true))
  }, [])

  // Real, computed-now operations data. No fake numbers — anything without a
  // real source is a labeled "coming soon" below, not a made-up value.
  const ops = useMemo(() => {
    const all = Object.entries(tournaments || {})
      .filter(([, v]) => v && typeof v === 'object')
      .map(([id, v]) => ({ id, info: v.info || {}, photos: v.photos || {} }))
      .filter((t) => !t.info.isTestStation)
    const activeReal = all.filter((t) =>
      (t.info.stage || 'registration') === 'active' && t.info.status !== 'closed' && !t.info.isDemo)
    const openRegs = all.filter((t) =>
      (t.info.stage || 'registration') === 'registration' && t.info.status !== 'closed'
      && !t.info.qualifyingEventId && !t.info.isDemo)
    const photoCount = all.reduce((n, t) => n + Object.keys(t.photos).length, 0)

    const qes = Object.values(qualifyingEvents || {})
      .filter((v) => v && typeof v === 'object' && !v.info?.builtTournamentId)
    const qualifyingInProgress = qes.filter((qe) => (qe.info?.status || 'open') !== 'closed')
    const readyToBuild = qes.filter((qe) => {
      const locked = (qe.info?.status || 'open') === 'closed'
      const approved = qe.teams
        ? Object.values(qe.teams).filter((tm) => tm && (tm.approved === true || tm.status === 'approved')).length
        : 0
      return locked && approved > 0
    })
    return {
      active: activeReal.length,
      openRegs: openRegs.length,
      qualifying: qualifyingInProgress.length,
      readyToBuild: readyToBuild.length,
      photos: photoCount,
    }
  }, [tournaments, qualifyingEvents])

  const now = new Date()
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening'
  const todayLong = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })

  const wCode = weather?.current?.weather_code
  const [wEmoji, wLabel] = WMO[wCode] || ['🌡️', '—']
  const wTemp = weather?.current?.temperature_2m != null ? `${Math.round(weather.current.temperature_2m)}°F` : null
  const sunrise = fmtTime(weather?.daily?.sunrise?.[0])
  const sunset = fmtTime(weather?.daily?.sunset?.[0])

  // Center strip — golf-relevant current metrics from Open-Meteo (real data).
  const cur = weather?.current
  const dailyNow = weather?.daily
  const compass = (deg) => (deg == null ? '' : ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'][Math.round(deg / 22.5) % 16])
  const r = (n) => (n == null ? null : Math.round(n))
  const wxMetrics = cur ? [
    { label: 'Wind', value: cur.wind_speed_10m != null ? `${r(cur.wind_speed_10m)} mph ${compass(cur.wind_direction_10m)}`.trim() : null },
    { label: 'Feels like', value: cur.apparent_temperature != null ? `${r(cur.apparent_temperature)}°` : null },
    { label: 'Humidity', value: cur.relative_humidity_2m != null ? `${r(cur.relative_humidity_2m)}%` : null },
    { label: 'Hi / Lo', value: dailyNow?.temperature_2m_max?.[0] != null ? `${r(dailyNow.temperature_2m_max[0])}° / ${r(dailyNow.temperature_2m_min[0])}°` : null },
    { label: 'Rain', value: dailyNow?.precipitation_probability_max?.[0] != null ? `${r(dailyNow.precipitation_probability_max[0])}%` : null },
  ].filter((m) => m.value != null) : []

  // 10-day forecast rows from Open-Meteo daily arrays (real data).
  const dd = weather?.daily
  const days = dd?.time
    ? dd.time.map((t, i) => ({
        date: t,
        name: i === 0 ? 'Today' : new Date(`${t}T00:00`).toLocaleDateString('en-US', { weekday: 'short' }),
        code: dd.weather_code?.[i],
        hi: dd.temperature_2m_max?.[i],
        lo: dd.temperature_2m_min?.[i],
        precip: dd.precipitation_probability_max?.[i],
        wind: dd.wind_speed_10m_max?.[i],
      }))
    : []

  // Next ~24 hours from now, sliced out of Open-Meteo's hourly arrays (real data).
  const hh = weather?.hourly
  const hours = hh?.time
    ? (() => {
        const nowMs = Date.now()
        let start = hh.time.findIndex((t) => new Date(t).getTime() >= nowMs - 3600e3)
        if (start < 0) start = 0
        return hh.time.slice(start, start + 24).map((t, k) => {
          const i = start + k
          return {
            time: t,
            code: hh.weather_code?.[i],
            temp: hh.temperature_2m?.[i],
            precip: hh.precipitation_probability?.[i],
            wind: hh.wind_speed_10m?.[i],
            isNow: k === 0,
          }
        })
      })()
    : []

  // Golf Pro Inbox — only items we can truly compute now.
  const inboxLive = []
  if (ops.openRegs > 0) inboxLive.push({ text: `${ops.openRegs} registration${ops.openRegs === 1 ? '' : 's'} open — collecting entries.` })
  if (ops.readyToBuild > 0) inboxLive.push({ text: `A qualifying field is approved & locked — ready to build the tournament.` })

  return (
    <PinGate>
      {/* Content only — the shared AdminShell provides the full-height sidebar +
          zoom frame (identical scaling on every page, like the 4-Ball site). */}
      <>
        <div className="pbcc-main-panel-4ball flex-1 flex flex-col min-h-0 min-w-0">
          <PbccHeroHeader
            variant="admin"
            showCommandCenter={false}
            onLock={() => { clearAdminSession(); location.reload() }}
            onSignOut={() => { clearAdminSession(); trialSignOut() }}
          />
          <div className="flex flex-1 min-h-0">
            <main className="max-w-6xl w-full mx-auto p-4 sm:p-5 space-y-6 flex flex-col flex-1 min-h-0">
              {/* This Golf Ops hub IS home — no "Back to Home" here (it was a no-op
                  pointing at this same page). Navigation is the sidebar tabs. */}

              {/* 1 — Welcome header (hidden on the standalone Weather page) */}
              {!weatherOnly && (
              <section
                className="rounded-2xl p-5 sm:p-6 flex items-center gap-4"
                style={IS_GOLF_ELITE ? GE_GLASS : { background: DEEP_GREEN, border: `1px solid ${ACCENT}`, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
                {!IS_GOLF_ELITE && <img src={CLUB.logoLight} alt="" className="h-24 w-24 sm:h-28 sm:w-28 object-contain shrink-0" />}
                <div className="min-w-0">
                  <p className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: IS_GOLF_ELITE ? '#1670ee' : ACCENT }}>
                    Golf Operations
                  </p>
                  <h1 className="text-xl sm:text-2xl font-extrabold leading-tight" style={IS_GOLF_ELITE ? { color: '#0a2b57' } : { color: '#fff', fontFamily: 'Cinzel, serif' }}>
                    {greeting} — {CLUB.name}
                  </h1>
                  <p className="text-sm mt-0.5" style={{ color: IS_GOLF_ELITE ? '#4f6580' : 'rgba(255,255,255,0.7)' }}>{todayLong}</p>
                </div>
              </section>
              )}

              {/* 2 — Weather (removed on the 4-Ball-only site AND on Golf Elite) */}
              {!FOURBALL_ONLY && !IS_GOLF_ELITE && (
              <section>
                <PbccSectionTitle title="Weather" subtitle="Pine Bluff, AR · 10-day forecast + live radar" />

                {/* current conditions — always visible */}
                <div className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-2xl" style={{ background: DEEP_GREEN, border: `1px solid ${ACCENT}` }}>
                  <div className="flex items-center gap-3 min-w-0">
                    {weatherFailed ? (
                      <span className="text-sm text-white/70">Current conditions unavailable</span>
                    ) : weather ? (
                      <>
                        <span className="text-4xl leading-none">{wEmoji}</span>
                        <div>
                          <div className="text-2xl font-extrabold text-white leading-none">{wTemp || '—'}</div>
                          <div className="text-xs text-white/75 mt-0.5">{wLabel}</div>
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-white/60">Loading current conditions…</span>
                    )}
                  </div>

                  {/* center — golf-relevant current metrics, evenly spaced */}
                  {!weatherFailed && wxMetrics.length > 0 ? (
                    <div className="hidden sm:flex flex-1 flex-wrap items-center justify-evenly gap-x-4 gap-y-1 px-2">
                      {wxMetrics.map((m) => (
                        <div key={m.label} className="text-center leading-tight">
                          <div className="text-[10px] uppercase tracking-wider text-white/55">{m.label}</div>
                          <div className="text-sm font-extrabold text-white whitespace-nowrap">{m.value}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="text-right text-xs font-semibold shrink-0" style={{ color: ACCENT }}>
                    <div>🌅 {sunrise}</div>
                    <div className="mt-0.5">🌇 {sunset}</div>
                  </div>
                </div>

                {/* hourly — next ~24 hours, horizontal scroll */}
                {!weatherFailed && hours.length > 0 ? (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    {hours.map((h) => <HourTile key={h.time} h={h} />)}
                  </div>
                ) : null}

                {/* 10-day forecast — the lead, always visible */}
                <div className="mt-3">
                  {weatherFailed ? (
                    <p className="text-sm text-gray-500">10-day forecast unavailable.</p>
                  ) : days.length === 0 ? (
                    <p className="text-sm text-gray-400">Loading forecast…</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                      {days.map((d, i) => <ForecastTile key={d.date} d={d} today={i === 0} />)}
                    </div>
                  )}
                </div>

                {/* live radar — collapsed by default behind a slim toggle */}
                <div className="mt-3 rounded-2xl overflow-hidden" style={{ border: `1px solid ${ACCENT}33` }}>
                  <button
                    type="button"
                    onClick={toggleRadar}
                    aria-expanded={radarOpen}
                    className="w-full flex items-center justify-between px-4 py-2.5 transition"
                    style={{ background: 'var(--ge-cream-premium)' }}>
                    <span className="flex items-center gap-2 text-sm font-extrabold" style={{ color: PRIMARY }}>
                      <span className="text-base leading-none">📡</span>
                      {radarOpen ? 'Hide live radar' : 'Open live radar'}
                    </span>
                    <span className="text-sm" style={{ color: ACCENT }}>{radarOpen ? '▾' : '▸'}</span>
                  </button>
                  <div style={{ maxHeight: radarOpen ? 480 : 0, overflow: 'hidden', transition: 'max-height 400ms ease' }}>
                    {everOpened ? (
                      <iframe
                        title="Pine Bluff live radar"
                        src={WINDY_SRC}
                        className="w-full block"
                        style={{ height: 460, border: 0 }}
                        loading="lazy"
                      />
                    ) : null}
                  </div>
                </div>

                <p className="text-[11px] text-gray-400 mt-2">Tee times &amp; live rounds — coming soon.</p>
              </section>
              )}

              {/* 3 & 4 — Operations + Inbox (hidden on the standalone Weather page) */}
              {!weatherOnly && (<>
              {/* 3 — Status tiles (real data) */}
              <section>
                <PbccSectionTitle title="Operations" subtitle="Live counts — tap to open the Tournaments page." />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                  <StatTile to="/admin/pbcc-4ball" label="Active Tournaments" value={ops.active} />
                  <StatTile to="/admin/pbcc-4ball" label="Open Registrations" value={ops.openRegs} />
                  <StatTile to="/admin/pbcc-4ball" label="Qualifying in Progress" value={ops.qualifying} />
                  <StatTile to="/photos" label="Photos" value={ops.photos} />
                </div>
              </section>

              {/* 4 — Golf Pro Inbox (removed on the 4-Ball-only site) */}
              {!FOURBALL_ONLY && (
              <section className="pb-4">
                <PbccSectionTitle title="Golf Pro Inbox" subtitle="Your daily work queue." />
                <div className="mt-3 rounded-2xl overflow-hidden" style={IS_GOLF_ELITE ? GE_GLASS : { border: `1px solid ${ACCENT}33`, background: 'var(--ge-cream-premium)' }}>
                  {inboxLive.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Nothing needs attention right now — you're all caught up. ✅</div>
                  ) : (
                    inboxLive.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: ACCENT }} />
                        <span className="text-sm font-semibold" style={{ color: PRIMARY }}>{item.text}</span>
                      </div>
                    ))
                  )}
                  <InboxSoon label="Photos awaiting review" />
                  <InboxSoon label="Lesson requests" />
                  <InboxSoon label="Inventory / pro shop" />
                  <InboxSoon label="Gift cards" />
                </div>
              </section>
              )}
              </>)}

            </main>
          </div>
        </div>
      </>
      <AIAssistant tournamentId={null} />
    </PinGate>
  )
}

function HourTile({ h }) {
  const [emoji] = WMO[h.code] || ['🌡️', '']
  const label = h.isNow ? 'Now' : new Date(h.time).toLocaleTimeString('en-US', { hour: 'numeric' })
  return (
    <div
      className="shrink-0 w-16 rounded-xl px-2 py-2.5 text-center"
      style={{ background: 'var(--ge-cream-premium)', border: h.isNow ? `1.5px solid ${ACCENT}` : `1px solid ${ACCENT}30` }}>
      <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: h.isNow ? ACCENT : PRIMARY }}>{label}</div>
      <div className="text-xl leading-none my-1">{emoji}</div>
      <div className="text-sm font-extrabold" style={{ color: PRIMARY }}>{h.temp != null ? `${Math.round(h.temp)}°` : '—'}</div>
      <div className="text-[10px] mt-0.5" style={{ color: '#2563EB' }}>💧{h.precip != null ? `${h.precip}%` : '—'}</div>
      <div className="text-[10px] text-gray-500">💨{h.wind != null ? Math.round(h.wind) : '—'}</div>
    </div>
  )
}

function ForecastTile({ d, today }) {
  const [emoji] = WMO[d.code] || ['🌡️', '']
  return (
    <div
      className="rounded-xl p-3 text-center"
      style={{ background: 'var(--ge-cream-premium)', border: today ? `1.5px solid ${ACCENT}` : `1px solid ${ACCENT}30`, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
      <div className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: today ? ACCENT : PRIMARY }}>{d.name}</div>
      <div className="text-3xl leading-none my-1.5">{emoji}</div>
      <div className="text-sm font-extrabold" style={{ color: PRIMARY }}>
        {d.hi != null ? `${Math.round(d.hi)}°` : '—'}{' '}
        <span className="text-gray-400 font-semibold">{d.lo != null ? `${Math.round(d.lo)}°` : ''}</span>
      </div>
      <div className="text-xs mt-1" style={{ color: '#2563EB' }}>💧 {d.precip != null ? `${d.precip}%` : '—'}</div>
      <div className="text-xs text-gray-500 mt-0.5">💨 {d.wind != null ? `${Math.round(d.wind)} mph` : '—'}</div>
    </div>
  )
}

function ComingSoon() {
  return (
    <span className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
      style={{ background: 'rgba(var(--ge-primary-rgb),0.08)', color: '#6B7280' }}>
      Coming soon
    </span>
  )
}

// Links only when `to` is provided; otherwise a display-only tile (used so the
// operations counts show without becoming an entry point into the 4-Ball page).
function StatTile({ to, label, value }) {
  const style = { background: 'var(--ge-cream-premium)', border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)', textDecoration: 'none' }
  const body = (
    <>
      <div className="text-3xl font-extrabold leading-none" style={{ color: PRIMARY }}>{value}</div>
      <div className="text-xs font-semibold text-gray-600 mt-1.5">{label}</div>
      {to ? <div className="text-[11px] mt-2" style={{ color: ACCENT }}>View →</div> : null}
    </>
  )
  return to
    ? <Link to={to} className="rounded-2xl p-4 block transition active:scale-[0.99] hover:shadow-lg" style={style}>{body}</Link>
    : <div className="rounded-2xl p-4 block" style={style}>{body}</div>
}

function InboxSoon({ label }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: 'rgba(0,0,0,0.15)' }} />
      <span className="text-sm text-gray-500">{label}</span>
      <span className="ml-auto"><ComingSoon /></span>
    </div>
  )
}
